import React, { useState } from 'react';
import { Button, Space, Tooltip, Popconfirm, message } from 'antd';
import { PaperClipOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { partnerAPI } from '../services/api';

export interface Attachment {
  id: number;
  file_name: string;
  file_size: number;
  content_type: string;
  uploader_type: string;
  uploader_name: string;
  created_at?: string;
}

export const formatBytes = (n = 0) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

/** 첨부파일 칩 목록 — 클릭 시 인증 헤더로 blob 다운로드 */
const AttachmentList: React.FC<{
  items?: Attachment[];
  /** 본인 업로드 건에 삭제 버튼 노출 */
  canDelete?: boolean;
  onDeleted?: () => void;
  compact?: boolean;
}> = ({ items, canDelete, onDeleted, compact }) => {
  const [busy, setBusy] = useState<number | null>(null);
  if (!items || items.length === 0) return null;

  const download = async (a: Attachment) => {
    setBusy(a.id);
    try {
      const res = await partnerAPI.downloadInquiryFile(a.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = a.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('다운로드에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (a: Attachment) => {
    try {
      await partnerAPI.deleteInquiryFile(a.id);
      message.success('첨부파일을 삭제했습니다.');
      onDeleted?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  return (
    <div style={{ marginTop: compact ? 6 : 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((a) => (
        <Space
          key={a.id} size={4}
          style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '3px 8px', fontSize: 12, maxWidth: '100%',
          }}
        >
          <PaperClipOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
          <Tooltip title={`${a.file_name} · ${formatBytes(a.file_size)}`}>
            <span
              onClick={() => download(a)}
              style={{
                cursor: 'pointer', color: '#b45309', maxWidth: 220,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                display: 'inline-block', verticalAlign: 'middle',
              }}
            >
              {a.file_name}
            </span>
          </Tooltip>
          <span style={{ color: '#cbd5e1', fontSize: 11 }}>{formatBytes(a.file_size)}</span>
          <Button
            type="text" size="small" loading={busy === a.id}
            icon={<DownloadOutlined style={{ fontSize: 12 }} />}
            onClick={() => download(a)}
          />
          {canDelete && (
            <Popconfirm title="첨부파일을 삭제할까요?" onConfirm={() => remove(a)} okText="삭제" cancelText="취소">
              <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />} />
            </Popconfirm>
          )}
        </Space>
      ))}
    </div>
  );
};

export default AttachmentList;
